import { Box, Chip, Stack, Typography, useTheme, Divider } from '@mui/material'

import OutreachCallScriptPreview from '~/features/campaigns/components/ofuContacts/list/contactHistoryDialog/outreachCallScriptPreview'
import OutreachEmailPreview from '~/features/campaigns/components/ofuContacts/list/contactHistoryDialog/outreachEmailPreview'
import OutreachTextScriptPreview from '~/features/campaigns/components/ofuContacts/list/contactHistoryDialog/outreachTextScriptPreview'
import OFUSimulatePromptDetails from '~/features/campaigns/components/ofuSimulatePromptDetails'
import OFUSimulateStepConversation from '~/features/campaigns/components/ofuSimulateStepConversation'
import useIsAdmin from '~/features/campaigns/hooks/useIsAdmin'
import {
  ECampaignChannel,
  TCampaignCreatePayload,
  TOfuSimulateContact,
  TOfuSimulateOutreach,
} from '~/features/campaigns/models'
import { ICON_SIZE_BY_CHANNEL } from '~/features/campaigns/utils/constants'
import ChannelIcon from '~/features/inbox/components/channelIcon'
import { EChannelKeys } from '~/features/inbox/models'
import { formatLocaleDateTimeWithWeekday } from '~/utils/date'
import rem from '~/utils/ui'

type TOFUSimulateStepItem = {
  outreach: TOfuSimulateOutreach
  outreachNumber: number
  maxOutreach: number
  index: number
  handleUpdateOutreach: (outreach: TOfuSimulateOutreach, index: number) => void
  contact: TOfuSimulateContact
  campaign: TCampaignCreatePayload
  generationSystemPrompt?: string
  evaluationSystemPrompt?: string
}

export default function OFUSimulateStepItem({
  outreach,
  outreachNumber,
  maxOutreach,
  index,
  handleUpdateOutreach,
  contact,
  campaign,
  generationSystemPrompt = '',
  evaluationSystemPrompt = '',
}: TOFUSimulateStepItem) {
  const theme = useTheme()

  const isAdmin = useIsAdmin()

  return (
    <Stack>
      <Stack
        direction="row"
        alignItems="flex-start"
        gap={2}
        sx={{ width: '100%' }}
      >
        <Box
          sx={{
            width: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ChannelIcon
            channel={outreach.channel as unknown as EChannelKeys}
            width={
              ICON_SIZE_BY_CHANNEL[outreach.channel as unknown as EChannelKeys]
            }
            height={
              ICON_SIZE_BY_CHANNEL[outreach.channel as unknown as EChannelKeys]
            }
          />
        </Box>
        <Stack
          flex={1}
          sx={{
            borderRadius: rem(10),
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <Chip
              variant="outlined"
              label={outreach.channel}
              sx={{ textTransform: 'capitalize' }}
              size="small"
            />
            <Typography variant="body2">
              Outreach {outreachNumber} of {maxOutreach}
            </Typography>
          </Stack>
          <Typography variant="body2">
            {formatLocaleDateTimeWithWeekday(
              outreach.scheduledFor &&
                Number.isFinite(new Date(outreach.scheduledFor).getTime())
                ? outreach.scheduledFor
                : new Date(),
            )}
          </Typography>
          <Stack>
            {outreach.channel === ECampaignChannel.phone && (
              <OutreachCallScriptPreview outreach={outreach} />
            )}
            {outreach.channel === ECampaignChannel.email && (
              <OutreachEmailPreview outreach={outreach} />
            )}
            {outreach.channel === ECampaignChannel.text && (
              <OutreachTextScriptPreview outreach={outreach} />
            )}
          </Stack>

          {outreach.channel !== ECampaignChannel.phone && (
            <>
              <Divider sx={{ my: 2 }} />
              <OFUSimulateStepConversation
                outreach={outreach}
                index={index}
                handleUpdateOutreach={handleUpdateOutreach}
                contact={contact}
                campaign={campaign}
              />
            </>
          )}
        </Stack>
      </Stack>
      {isAdmin && (
        <OFUSimulatePromptDetails
          prompt={outreach.prompt || ''}
          systemPrompt={generationSystemPrompt}
          evaluationPrompt={outreach.evaluationPrompt}
          evaluationSystemPrompt={evaluationSystemPrompt}
        />
      )}
    </Stack>
  )
}
